import { create } from 'zustand';

const G_COUNTRY = 'ZA';

interface MarkerPosition {
    lat: number;
    lng: number;
}

interface State {
    markerPosition: MarkerPosition | null;
    address: string;
    addressSuggestions: string[];
    setMarkerPosition: (markerPosition: MarkerPosition | null) => void;
    setAddress: (address: string) => void;
    setAddressSuggestions: (suggestions: string[]) => void;
    isWithinCountry: (lat: number, lng: number) => Promise<boolean>;
    getAddressFromLatLng: (lat: number, lng: number) => Promise<void>;
    getAddressSuggestions: (input: string) => Promise<void>;
    geocodeAddress: (address: string) => Promise<MarkerPosition | null>;
    clearState: () => void;
}

export const useGoogleMapViewModel = create<State>((set, get) => ({
    markerPosition: null,
    address: '',
    addressSuggestions: [],

    setMarkerPosition: (markerPosition: MarkerPosition | null) =>
        set({ markerPosition }),

    setAddress: (address: string) => set({ address }),

    setAddressSuggestions: (suggestions: string[]) =>
        set({ addressSuggestions: suggestions }),

    isWithinCountry: async (lat: number, lng: number): Promise<boolean> => {
        if (!google.maps) return false;
        const geocoder = new google.maps.Geocoder();
        try {
            const result = await geocoder.geocode({ location: { lat, lng } });
            if (result.results && result.results.length > 0) {
                const addressComponents = result.results[0].address_components;
                const countryComponent = addressComponents.find(component =>
                    component.types.includes('country')
                );
                return countryComponent?.short_name === G_COUNTRY;
            }
            return false;
        } catch (error) {
            return false;
        }
    },

    geocodeAddress: async (address: string): Promise<MarkerPosition | null> => {
        if (!google.maps) return null;
        const geocoder = new google.maps.Geocoder();
        try {
            const result = await geocoder.geocode({
                address,
                componentRestrictions: { country: G_COUNTRY },
            });
            if (result.results && result.results.length > 0) {
                const location = result.results[0].geometry.location;
                return { lat: location.lat(), lng: location.lng() };
            }
            return null;
        } catch {
            return null;
        }
    },

    getAddressFromLatLng: async (lat: number, lng: number) => {
        const { setAddress } = get();

        if (!google.maps) {
            setAddress('');
            return;
        }
        const geocoder = new google.maps.Geocoder();
        try {
            const result = await geocoder.geocode({ location: { lat, lng } });
            if (result.results && result.results.length > 0) {
                setAddress(result.results[0].formatted_address);
            } else {
                setAddress('');
            }
        } catch (error) {
            setAddress('');
        }
    },

    getAddressSuggestions: async (input: string) => {
        const { setAddressSuggestions } = get();

        if (!google.maps || !input) {
            setAddressSuggestions([]);
            return;
        }
        try {
            const request: google.maps.places.AutocompleteRequest = {
                input,
                includedRegionCodes: [G_COUNTRY],
            };
            const response =
                await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                    request
                );
            setAddressSuggestions(
                response.suggestions.map(suggestion =>
                    suggestion.placePrediction
                        ? suggestion.placePrediction.text.toString()
                        : ''
                )
            );
        } catch {
            setAddressSuggestions([]);
        }
    },

    clearState: () =>
        set({
            markerPosition: null,
            address: '',
            addressSuggestions: [],
        }),
}));
